/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { TextField, ThemeProvider,  MenuItem,  Button, Box, FormControlLabel, Checkbox, Link, Autocomplete } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { Formik, FormikErrors, FormikHelpers } from 'formik';
import {addEmbryoTransfer, CycleEmbryoDetails, EmbryoTransferType, EmbryoTransferValidationSchema, getEmbryoTransfer,  getEmbryoTransferDetails,  initialEmbryoTransfer, updateEmbryoTransfer} from './embryoTransfer.helpers';
import { theme } from '../../Medical_History_Forms/themeprovider';
import { useAlert } from '../../alertProvider';
import { useLoader } from '../../loaderProvider/loader';
import {ConfirmationModal} from '../../confirmationModal';
import FormObserver from '../../FormikFormObserver';
import { capitalizeTitle, extractTime, RequiredLabel } from '../../../utils/commonUtils';
import { formatDateYMD, getDateWithOffset } from '../../../utils/dateUtils';
import { usePatientContext } from '../../SubPatinetProvider';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { useClinic } from '../../ClinicProvider';
import { getUsersByClinicId } from '../../../services/apiService';
import EmbryoDetailsTable from './embryoDetailsTable';
import { useNavigate } from 'react-router-dom';
import { Clinician, CycleData, CycleItem, getARTCycleList, MasterconfigrutationDetails, Permission, Role, ROLES } from '../oocyteRetrieval/OocyteRetrival.helper';
import StatusChip from '../../customTableComponent/statusChip';
import EmbryoTransferDetails from './embryoTransferDetails';
import EmbryoCycleUnavailable from '../../../pages/Embryology/Embryo_Culture/embryoCycleUnavailable';
import { useCycle } from '../../cycleContext/CycleContext';
import { useTranslation } from 'react-i18next';
import { usePatientContext as useMainPatientContext } from '../../patinetProvider';

interface EmbryoTransferFormProp {
  onFormChange?: (hasChanged: boolean) => void;
  dropDownOptions?: MasterconfigrutationDetails;
  permissions: Permission[];
  onRecordSaved?: (saved: boolean) => void;
}

const unavailableProcedures = ['(OI) Cycle', 'IUI', 'Donor Cryopreservation','Donor cycle', 'Cryopreservation'];
// 'Surrogacy Cycle'
const EmbryoTransfer: React.FC<EmbryoTransferFormProp> = ({ onFormChange,dropDownOptions, permissions, onRecordSaved }) => {
  const canEditOrAdd = permissions.some(p => p.access === 'edit' || p.access === 'add');
  const canView = permissions.some(p => p.access === 'view');

  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const handleFormChange = onFormChange || (() => {});
  const { patientId } = usePatientContext();
  const { addAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const [initialValues, setInitialValues] = useState<EmbryoTransferType>(initialEmbryoTransfer);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openResetConfirmDialog, setOpenResetConfirmDialog] = useState(false); 
  const { ClinicId } = useClinic();
  const navigate = useNavigate();
  const [cycleStatus, setCycleStatus] = useState<string | null>(null);
  const { patientId: mainPatientId} = useMainPatientContext();

  const [embryoTransferDataAvailable, setEmbryoTransferDataAvailable ] = useState<boolean>(false); // New state variable
  const [isEditing, setIsEditing] = useState(false);
  const isFormDisabled = embryoTransferDataAvailable && !isEditing;

  const [cycleList, setCycleList] = useState<CycleItem[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<number | null>(null);
  const [embryoDetails, setEmbryoDetails] = useState<CycleEmbryoDetails[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedCycleItem, setSelectedCycleItem] = useState<any | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedCycleInfo, setSelectedCycleInfo] = useState<any | null>(null);

  const [showActionButtons, setShowActionButtons] = useState<boolean>(false);
  const { setCycleId, setSurgeryCycleId } = useCycle();


  console.log('permissions:', permissions);
  
  const sanitizeEmbryoTransfer = (values: EmbryoTransferType): EmbryoTransferType => {
    const sanitizedValues = { ...values };
    if (sanitizedValues.start_time !== null && sanitizedValues.start_time.trim().length === 5) {
      sanitizedValues.start_time = `${sanitizedValues.start_time}:00`;
    }
    if (sanitizedValues.end_time !== null && sanitizedValues.end_time.trim().length === 5) {
      sanitizedValues.end_time = `${sanitizedValues.end_time}:00`;
    }

    return sanitizedValues;
  };
  
  const fetchOocyteRetrieval = useCallback(
    (cycleId: number ) => {
      showLoader();   
      if (cycleId) {
        getEmbryoTransfer(cycleId) 
          .then((data) => {
            if (data) {
              setInitialValues({ ...initialEmbryoTransfer, ...data });
              setEmbryoTransferDataAvailable (true);
              onRecordSaved?.(true); // Notify parent if record exists
            } else {
              setInitialValues({ ...initialEmbryoTransfer });
              setEmbryoTransferDataAvailable (false);
              onRecordSaved?.(false); // Notify parent that no record exists
            }
          })
          .catch(() => {
            setEmbryoTransferDataAvailable (false);
            onRecordSaved?.(false); // Notify parent that no record exists
          })
          .finally(() => {
            hideLoader();
            setInitialValues((prevValues) => ({
              ...prevValues,
              cycle: cycleId
            }));
            if ( canView && !canEditOrAdd) {
              setIsEditing(false);
            }
          });

        getEmbryoTransferDetails(cycleId)
          .then((data) => {                
            setEmbryoDetails(data || []); 
          })
          .catch((error) => {
            console.error('Error fetching embryo details:', error);
            setEmbryoDetails([]); 
          });
      }
    },
    [showLoader, hideLoader, canEditOrAdd, canView, onRecordSaved] 
  );  

  const handleSubmit = async (
    values: EmbryoTransferType,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<EmbryoTransferType>
  ) => {
    showLoader();

    // Use the utility function to sanitize values
    const sanitizedValues = sanitizeEmbryoTransfer(values);

    const cycleId = sanitizedValues.cycle ?? 0;
    
    const operation = isEditing
      ? updateEmbryoTransfer(sanitizedValues?.id as number, sanitizedValues)
      : addEmbryoTransfer(sanitizedValues);
    operation
      .then((updatedData) => {
        addAlert(tGeneral('embryoTransferSaved'), 'success');
        setInitialValues(updatedData);
        resetForm({ values: updatedData });
        fetchOocyteRetrieval(cycleId);
        setIsEditing(false);
        onRecordSaved?.(true); // Notify parent that record is saved
      })
      .catch((error) => {
        if (error.response?.data?.type === 'validation_error') {
          const apiErrors = error.response.data.errors;
          const formikErrors: FormikErrors<EmbryoTransferType> = {};
          apiErrors.forEach((error: { attr: keyof EmbryoTransferType; detail: string }) => {
            formikErrors[error.attr] = error.detail;
          });

          setErrors(formikErrors);
        } else {
           
          console.error('An error occurred while saving EmbryoTransfer.');
        }
        addAlert(
	          error?.response?.data?.errors?.[0]?.detail ??
	          error?.response?.data?.message ??
	          error?.message ??
	          'An unexpected error occurred.',
	          'error'
	        );
      })
      .finally(() => {
        hideLoader();
        setSubmitting(false);
      });
  };
  
  const handleReset = (
    resetForm: FormikHelpers<EmbryoTransferType>['resetForm'],
    setFieldValue: FormikHelpers<EmbryoTransferType>['setFieldValue']
  ) => {
    setOpenResetConfirmDialog(false); 
    resetForm(); 
    
    Object.keys(initialEmbryoTransfer).forEach((field) => {
      const initialValue = initialEmbryoTransfer[field as keyof EmbryoTransferType];
      if (typeof initialValue === 'number' || initialValue === null) {
        setFieldValue(field, '');
      } else {
        setFieldValue(field, initialValue);
      }
    });
  }; 
  
  useEffect(() => {
    showLoader();
    getARTCycleList(patientId)
      .then((data) => {   
        const formattedData = data.map((cycle: CycleData) => ({
          id: cycle.id,
          value: cycle.cycle_number,
          label: cycle.cycle_number,
          status: cycle.status,
          procedure:cycle?.procedure?.procedures,
          procedure_list: cycle?.procedure,
          is_surrogate: cycle?.is_surrogate
        }));
        setCycleList(formattedData);

        // Set default cycle if the list is not empty 
        if (formattedData.length > 0) {
          const procedures = data[0].procedure?.procedures;
          // If procedures include "FET", set the last cycle data
          if (procedures === 'fet') {
            const lastCycle = data[0] ;               //Requirement chnaged so index changed 1 to 0
            setSelectedCycle(lastCycle.id); 
            setSurgeryCycleId(lastCycle.id);
            setCycleStatus(lastCycle.status); 
            fetchOocyteRetrieval(lastCycle.id);            
            setSelectedCycleItem(lastCycle.procedure_list);
            setShowActionButtons(!unavailableProcedures.includes(lastCycle.procedure));
            setInitialValues((prevValues) => ({
              ...prevValues,
              cycle_start_date: data[0]?.cycle_start_date,
              oocyte_fertilized_method: data[0]?.fertilized_count ?? 0,
              embryo_cryo_preservation_method: data[0]?.cryopreserved_count ?? 0,
              fresh_embryo_transfer_method: data[0]?.fresh_transfer_count ?? 0,
              frozen_embryo_transfer_method: data[0]?.frozen_transfer_count ?? 0,
            }));
          } else {
          // If not "FET", use the first cycle by default
            setSelectedCycle(formattedData[0].id);
            setCycleStatus(formattedData[0].status);
            fetchOocyteRetrieval(formattedData[0].id);            
            setSurgeryCycleId(formattedData[0].id);
            setSelectedCycleItem(formattedData[0].procedure_list);
            setShowActionButtons(!unavailableProcedures.includes(formattedData[0].procedure));
            setSelectedCycleInfo(formattedData[0]);
            
          }

          
          setInitialValues((prevValues) => ({
            ...prevValues,
            cycle_start_date: data[0]?.cycle_start_date,
            oocyte_fertilized_method: data[0]?.fertilized_count ?? 0,
            embryo_cryo_preservation_method: data[3]?.cryopreserved_count ?? 0,
            fresh_embryo_transfer_method: data[0]?.fresh_transfer_count ?? 0,
            frozen_embryo_transfer_method: data[0]?.frozen_transfer_count ?? 0,
          }));
          

          // Get appointment data from et_event first, fallback to opu_event, then use current date/time
          const etAppointment = data[0]?.et_event?.appointment;
          const opuAppointment = data[0]?.opu_event?.appointment;
          const appointment = etAppointment || opuAppointment;

          setInitialValues((prevValues) => ({
            ...prevValues,
            date: appointment?.appointment_date 
              ? appointment.appointment_date 
              : new Date().toISOString().split('T')[0], // Defaults to current date (YYYY-MM-DD)                      
            embryologist: appointment?.embryologist
              ? Number(appointment.embryologist)
              : null,          
            start_time: extractTime(appointment?.start_datetime) 
                        ?? new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }), // Defaults to current time with AM/PM
            end_time: extractTime(appointment?.end_datetime) 
                        ?? new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }), // Defaults to current time with AM/PM
                    
          })); 
          
        }
      })
      .catch(() => {
        console.error('Error fetching cycle list');
      })
      .finally(() => {
        hideLoader();
        setIsEditing(false);
      });
  }, [patientId, showLoader, hideLoader,setSurgeryCycleId, fetchOocyteRetrieval]);

 
  const handleCycleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = Number(event.target.value);        
    const foundCycleItem = cycleList.find((item) => item.id === selectedValue);    
    setCycleId(selectedValue);
    setSurgeryCycleId(selectedValue);
    setSelectedCycleItem(foundCycleItem || null);

    setShowActionButtons(!unavailableProcedures.includes(foundCycleItem?.procedure || ''));
    setSelectedCycleInfo(foundCycleItem || null);    
    setSelectedCycle(selectedValue);
    setCycleStatus(foundCycleItem?.status || null);     
    fetchOocyteRetrieval(selectedValue);
    // Only proceed if procedure is defined and does not include 'FET'. 
    // Code commented because requirement changed
    
    // if (
    //   (foundCycleItem?.procedure && !foundCycleItem.procedure.includes('FET')) ||
    //   !foundCycleItem?.procedure
    // ) {
    //   setSelectedCycle(selectedValue);
    //   setCycleStatus(foundCycleItem?.status || null);    
    //   fetchOocyteRetrieval(selectedValue);
    // }
  };

  // useEffect(() => {
  //   return () => {
  //     setCycleId(null); 
  //   };
  // }, [setCycleId]);
  
  const handleCombinedChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formikHandleChange: (event: React.ChangeEvent<any>) => void
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formikHandleChange(event);
    handleCycleChange(event as React.ChangeEvent<HTMLInputElement>);
    setIsEditing(false);
  };

  // Initialize state for doctor lists
  const [doctorList, setDoctorList] = useState<Record<Role, Clinician[]>>({
    DOCTOR: [],
    ANESTHETIST: [],
    EMBRYOLOGIST: [],
    NURSE: [],
  });
  const fetchCliniciansByRole = useCallback((role: Role) => {
    showLoader();
    const query = `clinic=${ClinicId}&role=${role}`;
    getUsersByClinicId(query)
      .then((res) => {
        setDoctorList(prev => ({
          ...prev,
          [role]: res?.objects ?? [],
        }));
      })
      .catch((error) => {
        console.error(`Error fetching ${role.toLowerCase()}s:`, error);
      })
      .finally(() => {
        hideLoader();
      });
  }, [ClinicId, showLoader, hideLoader]); 

  // Fetch clinicians for each role on component mount or when ClinicId changes
  useEffect(() => {
    ROLES.forEach(fetchCliniciansByRole);
  }, [fetchCliniciansByRole]); // Use the memoize

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const navigateToCycleOverview = (cycleId:number|null) => {
    
    if (cycleId) {
      navigate(`/clinical/patients/cycle-overview/${mainPatientId}`, { state: { cycleId: cycleId } });      
    } else {      
      navigate(`/clinical/patients/cycle-overview/${mainPatientId}`);
    }
  };

  
  const isEmbryoTransferUnavailable = () => {
    const donorInfo = selectedCycleItem?.donor_info;
    if (!donorInfo) return false;
    const recipientType = donorInfo.recipient?.type;
    // Surrogate condition
    if (recipientType === 'SURROGATE' && selectedCycleInfo?.is_surrogate) {
      setShowActionButtons(false);
      return true;
    } else {
      setShowActionButtons(true);
    }
    return false;
  };

  // Helper to check surrogate condition and return message
  const getEmbryoTransferUnavailableMsg = () => {
    const donorInfo = selectedCycleItem?.donor_info;
    const recipientType = donorInfo?.recipient?.type;
    if (recipientType === 'SURROGATE' && selectedCycleInfo?.is_surrogate) {
      return tGeneral('embryoTransferNotPrescribed');
    }
    return tGeneral('embryoTransferNotPrescribed');
  };

  return (
    <Formik 
      initialValues={initialValues}
      validationSchema={EmbryoTransferValidationSchema(tGeneral)}
      onSubmit={handleSubmit}
      enableReinitialize
    >      
      {({         
        values,
        setFieldValue,
        submitForm,
        validateForm,
        errors,
        dirty,  handleBlur, handleChange, handleSubmit, isSubmitting, touched, resetForm, setFieldTouched }) => (
        <>        
          <FormObserver dirty={dirty} onFormChange={handleFormChange} />          
          <ThemeProvider theme={theme}>
            <form onSubmit={handleSubmit}>
              <Box sx={{flexGrow: 1, marginRight:2}}>
                <Grid2 container spacing={2}  sx={{ marginTop: 1 }}>
                  {/* Cycle No. */}
                  <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={RequiredLabel(tGeneral('cycleNo'), true)}
                      name="cycle"
                      select
                      value={values.cycle || ''}
                      onBlur={handleBlur}
                      onChange={handleCombinedChange(handleChange)}
                      error={touched.cycle && Boolean(errors.cycle)}
                      // helperText={touched.cycle && errors.cycle}
                    >
                      {cycleList.map((item) => ( 
                        <MenuItem key={item.value} value={item.id}>
                          {item.label} &nbsp;|&nbsp; &nbsp; 
                          <StatusChip 
                            value={item.status === 'TERMINATED' ? 'Inactive' : capitalizeTitle(item.status)} 
                          />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid2>

                  <Grid2 size={{ xs: 4, sm:4, md: 4, lg:3 }} 
                    sx={{ textAlign: 'left' }}>
                    <Link
                      href="#"
                      onClick={()=>navigateToCycleOverview(values.cycle)}
                      sx={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, lineHeight: '23.2px', textAlign: 'left', marginLeft:2}}
                    >
                      {tGeneral('cycleOverview')}
                    </Link>
                  </Grid2>


                  {/* Terminate Cycle (Button) */}                  
                  {cycleStatus !== 'TERMINATED' ? (
                    <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6 }}
                      sx={{ textAlign: 'right', }}
                    >
                      <Button variant='outlined'  color='error' onClick={()=>navigateToCycleOverview(null)}   
                        sx={{
                          textTransform: 'capitalize',
                          borderRadius: '12px',
                        }}>
                        {tGeneral('terminateCycle')}
                      </Button>
                    </Grid2>
                  ) : (
                    <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6 }}
                      sx={{ textAlign: 'right'}}
                    >
                      <button className={styles.terminateButton} onClick={()=>navigateToCycleOverview(null)}>
                        {tGeneral('createNewCycle')}
                      </button>
                    </Grid2>
                  )}

                </Grid2>
                
                {
                  selectedCycleItem && (
                    // (unavailableProcedures.includes(selectedCycleItem?.procedures))
                    (unavailableProcedures.includes(selectedCycleItem?.procedures) ||
                      unavailableProcedures.includes(selectedCycleItem?.procedure) ||
                      isEmbryoTransferUnavailable())
                      ? (
                        <EmbryoCycleUnavailable
                          noContentMsg={getEmbryoTransferUnavailableMsg()}
                        />
                      )
                      : (cycleStatus === 'Inactive' || cycleStatus === 'TERMINATED' ||(embryoTransferDataAvailable && !isEditing)|| isFormDisabled ? (
                        <EmbryoTransferDetails cycleId={selectedCycle} dropDownOptions= {dropDownOptions} />
                      ) :  (
                        <>
                          <Grid2 container spacing={2}  sx={{ marginTop: 2 }}>
                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={RequiredLabel(tGeneral('transferMethod'), true)} 
                                name="transfer_method"
                                select
                                value={values.transfer_method || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.transfer_method && Boolean(errors.transfer_method)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.transfer_method && errors.transfer_method}
                              >
                                {dropDownOptions?.transfer_method.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid2>
                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>
                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('oocyteFertilized')}
                                name="oocyte_fertilized_method"
                                type="number"
                                value={values.oocyte_fertilized_method ?? 0}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                disabled={!canEditOrAdd && canView}

                                error={touched.oocyte_fertilized_method&& Boolean(errors.oocyte_fertilized_method)}
                                slotProps={{
                                  htmlInput: {readOnly : true, tabIndex: -1}
                                }}
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  cursor: 'not-allowed',
                                  pointerEvents: 'none' 
                                }}
                                // helperText={touched.oocyte_fertilized_method && errors.oocyte_fertilized_method}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('embryoCryopreserved')}
                                name="embryo_cryo_preservation_method"
                                type="number"
                                value={values.embryo_cryo_preservation_method ?? 0}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                disabled={!canEditOrAdd && canView}
                                error={touched.embryo_cryo_preservation_method && Boolean(errors.embryo_cryo_preservation_method)}
                                slotProps={{
                                  htmlInput: {readOnly : true, tabIndex: -1}
                                }}
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  cursor: 'not-allowed',
                                  pointerEvents: 'none' // Disables click interaction
                                }}
                                // helperText={touched.embryo_cryo_preservation_method && errors.embryo_cryo_preservation_method}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('freshTransfer')}
                                name="fresh_embryo_transfer_method"
                                type="number"
                                value={values.fresh_embryo_transfer_method ?? 0}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.fresh_embryo_transfer_method && Boolean(errors.fresh_embryo_transfer_method)}
                                disabled={!canEditOrAdd && canView}

                                slotProps={{
                                  htmlInput: {readOnly : true, tabIndex: -1}
                                }}
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  cursor: 'not-allowed',
                                  pointerEvents: 'none' // Disables click interaction
                                }}
                                // helperText={touched.fresh_embryo_transfer_method && errors.fresh_embryo_transfer_method}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('frozenTransfer')}
                                name="frozen_embryo_transfer_method"
                                type="number"
                                value={values.frozen_embryo_transfer_method ?? 0}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                disabled={!canEditOrAdd && canView}
                                error={touched.frozen_embryo_transfer_method && Boolean(errors.frozen_embryo_transfer_method)}
                                slotProps={{
                                  htmlInput: {readOnly : true, tabIndex: -1}
                                }}
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  cursor: 'not-allowed',
                                  pointerEvents: 'none' // Disables click interaction
                                }}
                                // helperText={touched.frozen_embryo_transfer_method && errors.frozen_embryo_transfer_method}
                              />
                            </Grid2>
                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>
                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={RequiredLabel(tGeneral('date'), true)}
                                name="date"
                                type="date"
                                value={values.date || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.date && Boolean(errors.date)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.date && errors.date}                                
                                slotProps={{ inputLabel: { shrink: true } }}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={RequiredLabel(tGeneral('startTime'), true)}
                                name="start_time"
                                type="time"
                                value={values.start_time || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.start_time && Boolean(errors.start_time)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.start_time && errors.start_time}                              
                                slotProps={{ inputLabel: { shrink: true } }}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={RequiredLabel(tGeneral('endTime'), true)}
                                name="end_time"
                                type="time"
                                value={values.end_time || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.end_time && Boolean(errors.end_time)}
                                helperText={
                                  touched.end_time &&
                                (errors.end_time === tGeneral('endTimeAfterStartTime')
                                  ? errors.end_time
                                  : '')
                                }                                                  
                                disabled={!canEditOrAdd && canView}
                                slotProps={{ inputLabel: { shrink: true } }}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('etDishId')}
                                type='number'
                                name="et_dish_id"
                                value={values.et_dish_id || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.et_dish_id && Boolean(errors.et_dish_id)}
                                helperText={touched.et_dish_id && errors.et_dish_id}
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>
                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <Autocomplete
                                fullWidth
                                size="small"
                                id="clinician"                      
                                options={doctorList['DOCTOR'] || []} 
                                getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                                value={doctorList['DOCTOR'].find((doctor) => doctor.id === values.clinician) || null}
                                onChange={(_, value) => {
                                  setFieldValue('clinician', value?.id || '');
                                }}
                                disabled={!canEditOrAdd && canView}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={RequiredLabel(tGeneral('clinician'), false)}
                                    error={touched.clinician && Boolean(errors.clinician)}
                                    // helperText={touched.clinician && errors.clinician}
                                    disabled={!canEditOrAdd && canView}
                                  />
                                )}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <Autocomplete
                                fullWidth
                                size="small"
                                id="embryologist"                      
                                options={doctorList['EMBRYOLOGIST'] || []} 
                                getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                                value={doctorList['EMBRYOLOGIST'].find((doctor) => doctor.id === values.embryologist) || null}
                                onChange={(_, value) => {
                                  setFieldValue('embryologist', value?.id || '');
                                }}
                                disabled={!canEditOrAdd && canView}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={RequiredLabel(tGeneral('embryologist'), true)}
                                    error={touched.embryologist && Boolean(errors.embryologist)}
                                    // helperText={touched.embryologist && errors.embryologist}
                                    disabled={!canEditOrAdd && canView}
                                  />
                                )}
                              />
                            </Grid2>

                  

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <Autocomplete
                                fullWidth
                                size="small"
                                id="witness_embryologist"
                                options={doctorList['EMBRYOLOGIST'] || []}
                                getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                                value={doctorList['EMBRYOLOGIST'].find((doctor) => doctor.id === values.witness_embryologist) || null}
                                onChange={(_, value) => {
                                  setFieldValue('witness_embryologist', value?.id || '');
                                }}
                                disabled={!canEditOrAdd && canView}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={RequiredLabel(tGeneral('witnessEmbryologist'), true)}
                                    error={touched.witness_embryologist && Boolean(errors.witness_embryologist)}                                   
                                    helperText={
                                      touched.witness_embryologist &&
                                      errors.witness_embryologist === tGeneral('witnessEmbryologistError')
                                        ? errors.witness_embryologist
                                        : ''
                                    }
                                    disabled={!canEditOrAdd && canView}
                                  />
                                )}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <Autocomplete
                                fullWidth
                                size="small"
                                id="nurse"
                                options={doctorList['NURSE'] || []}
                                getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                                value={doctorList['NURSE'].find((doctor) => doctor.id === values.nurse) || null}
                                onChange={(_, value) => {
                                  setFieldValue('nurse', value?.id || '');
                                }}
                                disabled={!canEditOrAdd && canView}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={RequiredLabel(tGeneral('nurse'), true)}
                                    error={touched.nurse && Boolean(errors.nurse)}
                                    // helperText={touched.nurse && errors.nurse}
                                    disabled={!canEditOrAdd && canView}
                                  />
                                )}
                              />
                            </Grid2>

                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>                            
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.performed_under_anesthesia || false}
                                    onChange={handleChange}
                                    name="performed_under_anesthesia"
                                    color="primary"
                                    disabled={!canEditOrAdd && canView}
                                  />
                                }
                                label={tGeneral('performedUnderAnesthesia')}
                              />
                            </Grid2>
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <Autocomplete
                                fullWidth
                                size="small"
                                id="anesthesia"
                                options={dropDownOptions?.embryo_Anesthesia ?? []} 
                                getOptionLabel={(option) => option.label} 
                                value={dropDownOptions?.embryo_Anesthesia.find((item) => item.value === values.anesthesia) || null} 
                                onChange={(_, value) => {
                                  setFieldValue('anesthesia', value?.value || ''); 
                                }}
                                disabled={!canEditOrAdd && canView}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={tGeneral('anesthesia')}
                                    error={touched.anesthesia && Boolean(errors.anesthesia)}
                                    // helperText={touched.anesthesia && errors.anesthesia}
                                    disabled={!canEditOrAdd && canView}
                                  />
                                )}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <Autocomplete
                                fullWidth
                                size="small"
                                id="anesthetist"
                                options={doctorList['ANESTHETIST'] || []}
                                getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                                value={doctorList['ANESTHETIST'].find((doctor) => doctor.id === values.anesthetist) || null}
                                onChange={(_, value) => {
                                  setFieldValue('anesthetist', value?.id || '');
                                }}
                                disabled={!canEditOrAdd && canView}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={tGeneral('anesthetist')}
                                    error={touched.anesthetist && Boolean(errors.anesthetist)}
                                    // helperText={touched.anesthetist && errors.anesthetist}
                                    disabled={!canEditOrAdd && canView}
                                  />
                                )}
                              />
                            </Grid2>

                            {/* Endometrium Thickness */}
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('endometriumThickness')}
                                name="endometrium_thickness"
                                value={values.endometrium_thickness || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.endometrium_thickness && Boolean(errors.endometrium_thickness)}
                                helperText={touched.endometrium_thickness && errors.endometrium_thickness}
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>

                          </Grid2>
                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>
                            
                            {/* Endometrium Pattern Dropdown */}
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={tGeneral('endometriumPattern')}
                                name="endometrium_pattern"
                                value={values.endometrium_pattern || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.endometrium_pattern && Boolean(errors.endometrium_pattern)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.endometrium_pattern && errors.endometrium_pattern}
                              >
                                {dropDownOptions?.endometrium_pattern.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Ultrasound Checkbox */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.is_ultrasound || false}
                                    onChange={handleChange}
                                    name="is_ultrasound"
                                    color="primary"
                                    disabled={!canEditOrAdd && canView}
                                  />
                                }
                                label={tGeneral('ultrasound')}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Ultrasound Method Dropdown */}
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={tGeneral('ultrasoundMethod')}
                                name="ultrasound_method"
                                value={values.ultrasound_method || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.ultrasound_method && Boolean(errors.ultrasound_method)}
                                disabled={!values.is_ultrasound || (!canEditOrAdd && canView)}
                                sx={{
                                  backgroundColor: !values.is_ultrasound ? '#f0f0f0' : 'inherit',
                                  borderRadius: '12px',
                                }}
                                // helperText={touched.ultrasound_method && errors.ultrasound_method}
                              >
                                {dropDownOptions?.ultrasound_method.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Transfer Depth */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('transferDepth')}
                                name="transfer_depth"
                                value={values.transfer_depth || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.transfer_depth && Boolean(errors.transfer_depth)}
                                // helperText={touched.transfer_depth && errors.transfer_depth}
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>

                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>
                            
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Level of Difficulty Dropdown */}
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={tGeneral('levelOfDifficulty')}
                                name="difficulty_level"
                                value={values.difficulty_level || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.difficulty_level && Boolean(errors.difficulty_level)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.difficulty_level && errors.difficulty_level}
                              >
                                {dropDownOptions?.embryoDifficulty_level.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={tGeneral('catheter')}
                                name="catheter"
                                value={values.catheter || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.catheter && Boolean(errors.catheter)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.catheter && errors.catheter}
                              >
                                {dropDownOptions?.catheter.map((item) => (
                                  <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Catheter Tube  */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('catheterTube')}
                                name="catheter_tube"
                                type='number'
                                value={values.catheter_tube || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.catheter_tube && Boolean(errors.catheter_tube)}
                                // helperText={touched.catheter_tube && errors.catheter_tube}
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* catheter_remarks */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('catheterRemarks')}
                                name="catheter_remarks"
                                value={values.catheter_remarks || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.catheter_remarks && Boolean(errors.catheter_remarks)}
                                helperText={touched.catheter_remarks && errors.catheter_remarks}
                                multiline
                                minRows={1}  
                                maxRows={3}  
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>

                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginTop: 3 }}>
                            
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* No of Attempts Dropdown */}
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={tGeneral('noOfAttempts')}
                                name="no_of_attempts"
                                value={values.no_of_attempts || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.no_of_attempts && Boolean(errors.no_of_attempts)}
                                disabled={!canEditOrAdd && canView}
                                // helperText={touched.no_of_attempts && errors.no_of_attempts}
                              >
                                <MenuItem value={1}>1</MenuItem>
                                <MenuItem value={2}>2</MenuItem>
                                <MenuItem value={3}>3</MenuItem>
                                <MenuItem value={4}>4</MenuItem>
                              </TextField>
                            </Grid2>
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* distance_from_fundus  */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('distanceFromFundus')}
                                name="distance_from_fundus"
                                type='number'
                                value={values.distance_from_fundus || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.distance_from_fundus && Boolean(errors.distance_from_fundus)}
                                // helperText={touched.distance_from_fundus && errors.distance_from_fundus}
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>
                  
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* distance_from_fundus  */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('embryoRetained')}
                                name="embryo_retained"
                                type='number'
                                value={values.embryo_retained || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.embryo_retained && Boolean(errors.embryo_retained)}
                                // helperText={touched.embryo_retained && errors.embryo_retained}
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Use of Tenaculum Checkbox */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.is_tenaculum_used || false}
                                    onChange={handleChange}
                                    name="is_tenaculum_used"
                                    color="primary"
                                    disabled={!canEditOrAdd && canView}
                                  />
                                }
                                label={tGeneral('useOfTenaculum')}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Use of Stylet Checkbox */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.is_stylet_used || false}
                                    onChange={handleChange}
                                    name="is_stylet_used"
                                    color="primary"
                                    disabled={!canEditOrAdd && canView}
                                  />
                                }
                                label={tGeneral('useOfStylet')}
                              />
                            </Grid2>
                  
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* Use of Embryo Glue Checkbox */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.is_embryo_glue_used || false}
                                    onChange={handleChange}
                                    name="is_embryo_glue_used"
                                    color="primary"
                                    disabled={!canEditOrAdd && canView}
                                  />
                                }
                                label={tGeneral('useOfEmbryoGlue')}
                              />
                            </Grid2>
                          </Grid2>

                          <Grid2 container spacing={2}  sx={{ marginY: 3, marginRight:2 }}>                  
                            <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                              <EmbryoDetailsTable embryoDetails={embryoDetails} />
                            </Grid2>                      
                          </Grid2>
                  
                          <Grid2 container spacing={2}  sx={{ marginTop: 4 }}>
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* complications */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('complications')}
                                name="complications"
                                value={values.complications || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.complications && Boolean(errors.complications)}
                                helperText={touched.complications && errors.complications}
                                multiline
                                minRows={1}  
                                maxRows={3}  
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>

                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* notes */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('notes')}
                                name="notes"
                                value={values.notes || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.notes && Boolean(errors.notes)}
                                helperText={touched.notes && errors.notes}
                                multiline
                                minRows={1}  
                                maxRows={3}  
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>
            
                            <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
                              {(() => {
                                // ET date + 12 days
                                const minAfterET = getDateWithOffset(values.date, 12); // must be YYYY-MM-DD
                                const today = formatDateYMD(new Date());               // YYYY-MM-DD

                                // Past dates disabled AND also enforce ET+12 rule:
                                // min selectable date = max(today, ET+12)
                                const minSelectable =
                                  minAfterET && today > minAfterET ? today : minAfterET || today;

                                return (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label={tGeneral('pregnancyDate')}
                                    name="pregnancy_date"
                                    type="date"
                                    value={values.pregnancy_date || ''}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={touched.pregnancy_date && Boolean(errors.pregnancy_date)}
                                    helperText={touched.pregnancy_date && errors.pregnancy_date}
                                    slotProps={{
                                      inputLabel: { shrink: true },
                                      htmlInput: { min: minSelectable },
                                    }}
                                  />
                                );
                              })()}
                            </Grid2>            
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={values.is_cycle_closed || false}
                                    onChange={handleChange}
                                    name="is_cycle_closed"
                                    color="primary"
                                    disabled={!canEditOrAdd && canView}
                                  />
                                }
                                label={tGeneral('cycleClose')}
                              />
                            </Grid2>
                            <Grid2 size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                              {/* cycle_close_reason */}
                              <TextField
                                fullWidth
                                size="small"
                                label={tGeneral('cycleCloseReason')}
                                name="cycle_close_reason"
                                value={values.cycle_close_reason || ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.cycle_close_reason && Boolean(errors.cycle_close_reason)}
                                helperText={touched.cycle_close_reason && errors.cycle_close_reason}
                                multiline
                                minRows={1}  
                                maxRows={3}  
                                disabled={!canEditOrAdd && canView}
                              />
                            </Grid2>
                          </Grid2>
                        </>
                      )
                      )
                  )
                }

                
                {showActionButtons && (cycleStatus !== 'TERMINATED' && canEditOrAdd && (!embryoTransferDataAvailable || isEditing)) && (
                  <Box className={styles.customButtonDiv}>                  
                    <button
                      type="button"
                      className={styles.resetButton}
                      onClick={() => setOpenResetConfirmDialog(true)} 
                    >
                      {tGeneral('reset')}
                    </button>
                    <button
                      type="button"                      
                      className={`${styles.customButton} ${isSubmitting || !dirty ? styles.disabled : ''}`}
                      disabled={isSubmitting || !dirty} 
                      onClick={() => {                     
                        validateForm().then((formErrors) => {
                          if (Object.keys(formErrors || {}).length === 0 && !isSubmitting) {
                            setOpenConfirmDialog(true);
                          } else {                            
                            Object.keys(values).forEach((field) => {
                              setFieldTouched(`${field}`, true, false);
                            }); 
                          }
                        }).catch((err) => {
                          console.error('Error during form validation:', err);
                        });
                      }}                  
                    >
                      <span style={{ fontWeight: 'bold' }}>
                        {isSubmitting ? tGeneral('saving') : tGeneral('save')}
                      </span>
                    </button>                  
                  </Box>
                )}

                {showActionButtons &&(cycleStatus !== 'TERMINATED'&& canEditOrAdd &&  (embryoTransferDataAvailable && !isEditing)) && (
                  <Box className={styles.customButtonDiv}>
                    <button
                      type="button"
                      className={styles.customButton}
                      onClick={handleEditClick}
                    >
                      <span style={{ fontWeight: 'bold' }}>{tGeneral('edit')}</span>
                    </button>
                  </Box>
                )}

                <ConfirmationModal
                  open={openConfirmDialog}
                  handleClose={() => setOpenConfirmDialog(false)}
                  onConfirm={() => {
                    setOpenConfirmDialog(false);
                    submitForm(); 
                  }}
                  type="save"
                  title={tSurgery('embryoTransfer')}
                  contentMessage={tSurgery('confirmSaveEmbryoTransfer')}
                />
                <ConfirmationModal
                  open={openResetConfirmDialog}
                  handleClose={() => setOpenResetConfirmDialog(false)} 
                  onConfirm={() => handleReset(resetForm, setFieldValue)} 
                  title={tSurgery('embryoTransfer')}
                  contentMessage={tSurgery('confirmResetEmbryoTransfer')}
                  type={'delete'}
                />
              </Box>
              
            </form>
          </ThemeProvider>
        </>
      )}
    </Formik>
  );
};

export default EmbryoTransfer;